/**
 * WebGL Shader Presets - Fragment shaders for the triangle field renderer.
 *
 * Each preset defines a vertex + fragment shader pair that renders a
 * fullscreen triangle grid with different visual effects.
 * All shaders receive: u_time, u_resolution, u_mouse, u_preset_param
 */

export interface ShaderPreset {
  name: string;
  category: 'trinity' | 'organic' | 'energy' | 'data';
  description: string;
  fragment: string;
}

// Shared vertex shader: fullscreen quad
export const VERTEX_SHADER = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// Shared GLSL helpers injected before each fragment shader
const GLSL_HEADER = `
precision highp float;
varying vec2 v_uv;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_param;

#define PI 3.14159265359
#define SQRT3 1.7320508

// Triangle grid: returns (col, row, isDown) packed into vec3
// Equilateral triangle grid in screen space
vec3 triGrid(vec2 uv, float scale) {
  float h = SQRT3 * 0.5;
  vec2 p = uv * scale;
  float row = floor(p.y / h);
  float xOff = mod(row, 2.0) * 0.5;
  float col = floor(p.x - xOff);
  vec2 local = vec2(p.x - xOff - col, p.y / h - row);
  float isDown = step(local.x + local.y, 1.0);
  // Alternate: check if point is in upper or lower triangle
  float diag = local.y - (1.0 - local.x);
  float triType = step(0.0, diag); // 1 = upper-right tri
  return vec3(col + xOff, row, mod(col + row, 2.0));
}

// Equilateral triangle signed distance
float sdTriangle(vec2 p, float r) {
  float k = SQRT3;
  p.x = abs(p.x) - r;
  p.y = p.y + r / k;
  if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
  p.x -= clamp(p.x, -2.0 * r, 0.0);
  return -length(p) * sign(p.y);
}

// Hash functions
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float hash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

// Simplex-ish noise
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
`;

// ─── Shader Presets ──────────────────────────────────────────────────────────

const trinityGradient: ShaderPreset = {
  name: 'Trinity Gradient',
  category: 'trinity',
  description: 'Brand gradient mapped across a triangle grid with gentle animation',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  // Triangle grid
  float scale = 20.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;
  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  // Trinity brand colors
  vec3 lightBlue = vec3(0.106, 0.694, 0.906);  // #1BB1E7
  vec3 mediumBlue = vec3(0.0, 0.6, 0.855);     // #0099DA
  vec3 darkBlue = vec3(0.0, 0.376, 0.686);      // #0060AF
  vec3 deepBlue = vec3(0.078, 0.247, 0.569);    // #143F91

  // Gradient based on position + time
  float t = uv.x * 0.5 + uv.y * 0.3 + sin(u_time * 0.5 + id * 6.28) * 0.15;
  t = clamp(t, 0.0, 1.0);

  vec3 col1;
  if (t < 0.33) {
    col1 = mix(deepBlue, darkBlue, t / 0.33);
  } else if (t < 0.66) {
    col1 = mix(darkBlue, mediumBlue, (t - 0.33) / 0.33);
  } else {
    col1 = mix(mediumBlue, lightBlue, (t - 0.66) / 0.34);
  }

  // Triangle cell edge
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.05, min(local.x, min(local.y, 1.0 - local.x)));

  col1 *= 0.7 + 0.3 * edge;
  col1 += 0.02 * sin(u_time + id * 20.0);

  gl_FragColor = vec4(col1, 1.0);
}
`,
};

const neonEdges: ShaderPreset = {
  name: 'Neon Edges',
  category: 'energy',
  description: 'Glowing triangle outlines with electric pulse propagation',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 16.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float xOff = mod(row, 2.0) * 0.5;
  float col = floor(gp.x + xOff);
  float id = hash(vec2(col, row));

  // Local coordinates within cell
  vec2 local = vec2(fract(gp.x + xOff), fract(gp.y / h));

  // Distance to triangle edges (approximation)
  float d1 = abs(local.y);
  float d2 = abs(local.y - 1.0);
  float d3 = abs(local.x + local.y - 1.0) * 0.707;
  float d4 = abs(local.x - local.y) * 0.707;
  float edge = min(min(d1, d2), min(d3, d4));

  // Pulse wave
  float dist = length(p - vec2(aspect * 0.5, 0.5));
  float wave = sin(dist * 8.0 - u_time * 3.0) * 0.5 + 0.5;

  // Glow
  float glow = 0.008 / (edge + 0.008);
  glow *= 0.5 + 0.5 * wave;

  // Color: cyan to magenta based on angle
  float angle = atan(p.y - 0.5, p.x - aspect * 0.5);
  vec3 glowColor = mix(
    vec3(0.0, 0.8, 1.0),
    vec3(0.8, 0.0, 1.0),
    sin(angle + u_time * 0.5) * 0.5 + 0.5
  );

  vec3 bg = vec3(0.02, 0.03, 0.06);
  vec3 finalColor = bg + glowColor * glow;

  // Subtle fill on some triangles
  float fill = step(0.85, id) * 0.08;
  finalColor += glowColor * fill * wave;

  gl_FragColor = vec4(finalColor, 1.0);
}
`,
};

const thermalField: ShaderPreset = {
  name: 'Thermal Field',
  category: 'data',
  description: 'Heat-map visualization with flowing temperature gradients',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 18.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  // Temperature field: layered noise
  float temp = 0.0;
  temp += noise(p * 3.0 + u_time * 0.3) * 0.5;
  temp += noise(p * 6.0 - u_time * 0.2) * 0.25;
  temp += noise(p * 12.0 + u_time * 0.1) * 0.125;
  temp += id * 0.1;

  // Mouse heat source
  vec2 mp = u_mouse * vec2(aspect, 1.0);
  float mouseDist = length(p - mp);
  temp += 0.4 * exp(-mouseDist * mouseDist * 8.0);

  temp = clamp(temp, 0.0, 1.0);

  // Thermal colormap: black -> blue -> magenta -> red -> yellow -> white
  vec3 color;
  if (temp < 0.2) {
    color = mix(vec3(0.0, 0.0, 0.1), vec3(0.0, 0.0, 0.8), temp / 0.2);
  } else if (temp < 0.4) {
    color = mix(vec3(0.0, 0.0, 0.8), vec3(0.8, 0.0, 0.6), (temp - 0.2) / 0.2);
  } else if (temp < 0.6) {
    color = mix(vec3(0.8, 0.0, 0.6), vec3(1.0, 0.2, 0.0), (temp - 0.4) / 0.2);
  } else if (temp < 0.8) {
    color = mix(vec3(1.0, 0.2, 0.0), vec3(1.0, 0.9, 0.0), (temp - 0.6) / 0.2);
  } else {
    color = mix(vec3(1.0, 0.9, 0.0), vec3(1.0, 1.0, 0.9), (temp - 0.8) / 0.2);
  }

  // Triangle cell edges
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.03, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.85 + 0.15 * edge;

  gl_FragColor = vec4(color, 1.0);
}
`,
};

const holographic: ShaderPreset = {
  name: 'Holographic',
  category: 'energy',
  description: 'Iridescent rainbow shift with depth-like parallax on triangles',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 14.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  // Iridescence based on view angle simulation
  vec2 center = vec2(aspect * 0.5, 0.5);
  float viewAngle = length(p - center);
  float irid = viewAngle * 4.0 + u_time * 0.8 + id * 2.0;

  // Rainbow
  vec3 color;
  color.r = sin(irid) * 0.5 + 0.5;
  color.g = sin(irid + 2.094) * 0.5 + 0.5;
  color.b = sin(irid + 4.189) * 0.5 + 0.5;

  // Fresnel-like brightness at edges
  float fresnel = pow(viewAngle / (aspect * 0.5), 0.5);
  color = mix(color * 0.4, color, fresnel);

  // Specular highlights
  vec2 mp = u_mouse * vec2(aspect, 1.0);
  float spec = exp(-length(p - mp) * 4.0) * 0.6;
  color += vec3(spec);

  // Triangle grid overlay
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.02, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.6 + 0.4 * edge;

  // Subtle metallic base
  color = mix(vec3(0.15, 0.15, 0.2), color, 0.85);

  gl_FragColor = vec4(color, 1.0);
}
`,
};

const waveField: ShaderPreset = {
  name: 'Wave Field',
  category: 'organic',
  description: 'Ocean-like waves propagating across a triangle surface',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 22.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));
  float isUp = mod(col + row, 2.0);

  // Multiple wave sources
  float w1 = sin(length(p - vec2(0.2, 0.3)) * 12.0 - u_time * 2.5);
  float w2 = sin(length(p - vec2(aspect - 0.3, 0.7)) * 10.0 - u_time * 2.0);
  float w3 = sin(p.x * 6.0 + p.y * 4.0 - u_time * 1.5);
  float wave = (w1 + w2 + w3) / 3.0;

  // Height affects triangle brightness
  float heightVal = wave * 0.5 + 0.5;
  heightVal += id * 0.08;

  // Ocean palette
  vec3 deep = vec3(0.01, 0.04, 0.15);
  vec3 mid = vec3(0.0, 0.2, 0.5);
  vec3 bright = vec3(0.1, 0.7, 0.9);
  vec3 foam = vec3(0.8, 0.95, 1.0);

  vec3 color;
  if (heightVal < 0.3) {
    color = mix(deep, mid, heightVal / 0.3);
  } else if (heightVal < 0.6) {
    color = mix(mid, bright, (heightVal - 0.3) / 0.3);
  } else {
    color = mix(bright, foam, (heightVal - 0.6) / 0.4);
  }

  // Up/down triangle differentiation
  color *= 0.9 + 0.1 * isUp;

  // Grid edges
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.025, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.85 + 0.15 * edge;

  gl_FragColor = vec4(color, 1.0);
}
`,
};

const matrixRain: ShaderPreset = {
  name: 'Matrix Rain',
  category: 'data',
  description: 'Digital rain cascading through a triangle lattice',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 24.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  // Rain columns
  float colId = hash(vec2(col, 0.0));
  float speed = 1.0 + colId * 3.0;
  float rainY = fract(colId * 10.0 - u_time * speed * 0.15);

  // Distance from rain head
  float distFromHead = uv.y - rainY;
  if (distFromHead < 0.0) distFromHead += 1.0;

  // Trail
  float trail = exp(-distFromHead * 6.0);
  float head = exp(-distFromHead * 40.0);

  // Flicker
  float flicker = step(0.3, fract(id * 43.758 + u_time * (2.0 + id * 3.0)));

  // Color
  vec3 bg = vec3(0.0, 0.02, 0.0);
  vec3 trailColor = vec3(0.0, 0.4, 0.1) * trail * flicker;
  vec3 headColor = vec3(0.5, 1.0, 0.5) * head;

  vec3 color = bg + trailColor + headColor;

  // Grid edges (subtle)
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.02, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.8 + 0.2 * edge;

  gl_FragColor = vec4(color, 1.0);
}
`,
};

const aurora: ShaderPreset = {
  name: 'Aurora',
  category: 'organic',
  description: 'Northern lights flowing across the triangle field',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 16.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  // Aurora bands - vertical curtains
  float n1 = noise(vec2(p.x * 2.0, u_time * 0.3));
  float n2 = noise(vec2(p.x * 3.0 + 5.0, u_time * 0.2 + 10.0));
  float n3 = noise(vec2(p.x * 1.5 + 10.0, u_time * 0.4 + 20.0));

  // Vertical fade (aurora appears in upper portion)
  float yFade = smoothstep(0.1, 0.9, uv.y);
  yFade *= smoothstep(1.0, 0.7, uv.y);

  // Band intensity
  float band1 = exp(-pow((uv.y - 0.5 - n1 * 0.2) * 4.0, 2.0));
  float band2 = exp(-pow((uv.y - 0.6 - n2 * 0.15) * 5.0, 2.0));
  float band3 = exp(-pow((uv.y - 0.4 - n3 * 0.2) * 3.5, 2.0));

  // Aurora colors
  vec3 green = vec3(0.1, 0.8, 0.3);
  vec3 teal = vec3(0.0, 0.6, 0.7);
  vec3 purple = vec3(0.4, 0.1, 0.6);

  vec3 color = vec3(0.01, 0.01, 0.03); // dark sky
  color += green * band1 * 0.7;
  color += teal * band2 * 0.5;
  color += purple * band3 * 0.4;

  // Per-triangle variation
  color *= 0.8 + 0.2 * id;

  // Grid
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.03, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.8 + 0.2 * edge;

  // Stars
  float star = step(0.98, id) * step(0.5, 1.0 - max(band1, max(band2, band3)));
  color += vec3(star * 0.8);

  gl_FragColor = vec4(color, 1.0);
}
`,
};

const breathe: ShaderPreset = {
  name: 'Breathe',
  category: 'organic',
  description: 'Meditative breathing pattern with concentric triangle rings',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  float scale = 20.0;
  float h = SQRT3 * 0.5;
  vec2 gp = (p + vec2(aspect * 0.5, 0.5)) * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  // Distance from center
  float dist = length(p);

  // Breathing wave: expands and contracts
  float breathRate = 0.5; // slow breath
  float breathPhase = sin(u_time * breathRate * PI * 2.0) * 0.5 + 0.5;
  float ring = sin(dist * 20.0 - breathPhase * 8.0) * 0.5 + 0.5;

  // Color based on depth
  vec3 innerColor = vec3(0.106, 0.694, 0.906); // Trinity light blue
  vec3 outerColor = vec3(0.078, 0.247, 0.569); // Trinity deep blue
  vec3 color = mix(innerColor, outerColor, dist * 1.5);

  // Ring modulation
  color *= 0.5 + 0.5 * ring;

  // Fade out at edges
  float vignette = 1.0 - smoothstep(0.3, 0.8, dist);
  color *= vignette;

  // Per-triangle shimmer
  color += 0.03 * sin(u_time * 2.0 + id * 30.0);

  // Grid
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.03, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.8 + 0.2 * edge;

  gl_FragColor = vec4(max(color, vec3(0.0)), 1.0);
}
`,
};

const dataStream: ShaderPreset = {
  name: 'Data Stream',
  category: 'data',
  description: 'Flowing data packets visualized as lit triangles in a network',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float scale = 20.0;
  float h = SQRT3 * 0.5;
  vec2 gp = p * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));
  float id2 = hash(vec2(col + 100.0, row + 200.0));

  // Multiple data streams traveling in different directions
  float stream1 = fract(id * 7.0 + u_time * 0.4 * (0.5 + id2));
  float stream2 = fract(id2 * 11.0 - u_time * 0.3);

  // Packet pulse
  float packet1 = exp(-pow((stream1 - 0.5) * 6.0, 2.0));
  float packet2 = exp(-pow((stream2 - 0.5) * 8.0, 2.0));

  // Active triangles (not all participate)
  float active1 = step(0.6, id);
  float active2 = step(0.7, id2);

  // Base grid color
  vec3 bg = vec3(0.02, 0.04, 0.08);
  vec3 gridColor = vec3(0.04, 0.08, 0.15);

  // Data colors
  vec3 stream1Color = vec3(0.0, 0.6, 1.0) * packet1 * active1;
  vec3 stream2Color = vec3(0.0, 1.0, 0.6) * packet2 * active2;

  vec3 color = bg + stream1Color + stream2Color;

  // Always-on dim triangles
  color += gridColor * (0.1 + 0.05 * id);

  // Grid edges
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.015, min(local.x, min(local.y, 1.0 - local.x)));
  float edgeGlow = (1.0 - edge) * 0.1;
  color += vec3(0.0, 0.15, 0.3) * edgeGlow;
  color *= 0.85 + 0.15 * edge;

  gl_FragColor = vec4(color, 1.0);
}
`,
};

const pulseStar: ShaderPreset = {
  name: 'Pulse Star',
  category: 'energy',
  description: 'Radial energy bursts emanating from the center',
  fragment: `
void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  float scale = 18.0;
  float h = SQRT3 * 0.5;
  vec2 gp = (p + vec2(aspect * 0.5, 0.5)) * scale;

  float row = floor(gp.y / h);
  float col = floor(gp.x + mod(row, 2.0) * 0.5);
  float id = hash(vec2(col, row));

  float dist = length(p);
  float angle = atan(p.y, p.x);

  // Radial pulses
  float pulse1 = sin(dist * 15.0 - u_time * 4.0) * 0.5 + 0.5;
  float pulse2 = sin(dist * 10.0 - u_time * 3.0 + 1.0) * 0.5 + 0.5;

  // Star rays
  float rays = abs(sin(angle * 3.0 + u_time * 0.5));
  rays = pow(rays, 3.0);

  // Combine
  float energy = pulse1 * 0.5 + pulse2 * 0.3 + rays * 0.2;
  energy *= exp(-dist * 2.0); // falloff from center

  // Colors: warm core to cool edges
  vec3 core = vec3(1.0, 0.6, 0.1);
  vec3 mid = vec3(0.8, 0.1, 0.5);
  vec3 outer = vec3(0.1, 0.2, 0.8);

  vec3 color;
  if (dist < 0.2) {
    color = mix(core, mid, dist / 0.2);
  } else {
    color = mix(mid, outer, clamp((dist - 0.2) / 0.5, 0.0, 1.0));
  }

  color *= energy + 0.05;

  // Per-triangle random brightness
  color *= 0.8 + 0.4 * id * energy;

  // Grid
  vec2 local = fract(gp / vec2(1.0, h));
  float edge = smoothstep(0.0, 0.02, min(local.x, min(local.y, 1.0 - local.x)));
  color *= 0.8 + 0.2 * edge;

  gl_FragColor = vec4(color, 1.0);
}
`,
};

// ─── Export Registry ─────────────────────────────────────────────────────────

export const SHADER_PRESETS: ShaderPreset[] = [
  trinityGradient,
  neonEdges,
  thermalField,
  holographic,
  waveField,
  aurora,
  breathe,
  dataStream,
  matrixRain,
  pulseStar,
];

export const SHADER_CATEGORIES = ['trinity', 'organic', 'energy', 'data'] as const;

/**
 * Compile a full fragment shader source from a preset
 */
export function buildFragmentSource(preset: ShaderPreset): string {
  return GLSL_HEADER + preset.fragment;
}
